import _, {isNumber} from 'lodash';
import GlobalReduxStore from 'stores/globalReduxStore';
import {CHERRY_PICK_ONLY_LABWARES, TroughWellList, WellLabel} from 'contents/PlateTypeEditor/PlateTypeEditorEnum';
import CalculatedWell from 'components/Labware/CalculatedWell/CalculatedWell';
import {HOVERED_WELL_COLOR, INACTIVE_WELL_COLOR} from 'constants/wellColorEnum';
import {isOnSpearBioTrough} from 'lib/labware/spearBioLabwareHelper';
import WellItem from 'models/wellItem';
import {
  LabwareWellCount,
  SWIPING_DIRECTION_X,
  SWIPING_DIRECTION_Y,
  TaskType,
  TipChannelType,
  TransformSelectionPattern,
  TransformSelectionType,
} from '../../../constants/dataEnum';
import jsonCopy from '../../../lib/jsonCopy';
import {filterToArray, find, selectDistinct} from '../../../lib/objectHelper';

export function getQuadrant(row, column) {
  if (row % 2 === 1 && column % 2 === 1) {
    return 1;
  }

  if (row % 2 === 1 && column % 2 === 0) {
    return 2;
  }

  if (row % 2 === 0 && column % 2 === 0) {
    return 3;
  }

  if (row % 2 === 0 && column % 2 === 1) {
    return 4;
  }

  return -1;
}

export const isLabware384 = (labware) => labware.rowCount * labware.colCount === 384;
export const isLabwareTrough = (labware) => TroughWellList.includes(labware.labwareLabel);
export const isLabwareSingleWellTrough = (labware) => labware.isTrough && labware.rowCount * labware.colCount === 1;
export const isLabwareCherryPickOnly = (labware) => CHERRY_PICK_ONLY_LABWARES.includes(labware.labwareLabel);
export const isLabwareColumnBased = (labware) => labware.colCount >= labware.rowCount;

export const doesLabwareHaveSameDirectionBase = (labware1, labware2) => {
  const isSameBase = isLabwareColumnBased(labware1) === isLabwareColumnBased(labware2);
  const isEitherSingleWellTrough = isLabwareSingleWellTrough(labware1) || isLabwareSingleWellTrough(labware2);

  return isSameBase || isEitherSingleWellTrough;
};

export const convertRowToNumeric = (row) => parseInt(row.charCodeAt(0) - 64, 10);
const convertRowToAlphabet = (row) => String.fromCharCode(64 + parseInt(row, 10));

export const TASK_PATTERN = {
  ROW_BASE: 'ROW_BASE',
  COLUMN_BASE: 'COLUMN_BASE',
  NO_BASE: 'NO_BASE',
};

export const SKIP_WELL_MODE = {
  ROW: 'SKIP_ROW',
  COLUMN: 'SKIP_COLUMN',
  ALL: 'SKIP_ALL',
  SKIP: 'NO_SKIP',
};

export const OPTIMIZE_MODE = {
  ROW: 'ROW',
  COLUMN: 'COLUMN',
  ALL: 'ALL',
};

export const getSkipWellMode = (rowTipCount, columnTipCount, isShift, is384, forceNoSkip = false) => {
  let skipWellMode = SKIP_WELL_MODE.ALL;
  let noSkip = false;

  if (!isShift && forceNoSkip && is384) {
    if (rowTipCount === 1 && columnTipCount > 1) {
      skipWellMode = SKIP_WELL_MODE.COLUMN;
    } else if (rowTipCount > 1 && columnTipCount === 1) {
      skipWellMode = SKIP_WELL_MODE.ROW;
    } else if (rowTipCount === 1 && columnTipCount === 1) {
      skipWellMode = SKIP_WELL_MODE.SKIP;
    }
  }

  if (isShift && is384) {
    skipWellMode = SKIP_WELL_MODE.SKIP;
    noSkip = true;
  }

  if (!is384) {
    skipWellMode = SKIP_WELL_MODE.SKIP;
    noSkip = true;
  }

  return [skipWellMode, noSkip];
};

export const getSkipRowColumn = (skipWellMode) => {
  let skipRow = false;
  let skipColumn = false;

  if (skipWellMode === SKIP_WELL_MODE.COLUMN) {
    skipColumn = true;
  } else if (skipWellMode === SKIP_WELL_MODE.ROW) {
    skipRow = true;
  } else if (skipWellMode === SKIP_WELL_MODE.ALL) {
    skipRow = true;
    skipColumn = true;
  }

  return [skipRow, skipColumn];
};

export const normalizeHighlightedWell = (
  row,
  column,
  tipCount,
  labware,
  labwareIndex = 0,
  noSkip = false,
  maxPosition = null,
  minPosition = null,
  tasklist = [],
  skipWellMode = SKIP_WELL_MODE.ALL,
  isSourceTrough = false,
) => {
  const {row: rowTipCount, column: columnTipCount} = tipCount;
  let {rowCount: labwareRowCount, colCount: labwareColCount} = labware;
  const {isIFC, isSpearBio} = labware;

  if (isIFC && labwareIndex) {
    labwareRowCount = labware.labwareIndexes[labwareIndex].rowCount;
    labwareColCount = labware.labwareIndexes[labwareIndex].colCount;
  }
  const is384 = isLabware384(labware) || labwareIndex;

  const [skipRow, skipColumn] = getSkipRowColumn(skipWellMode);

  if (maxPosition?.row) {
    labwareRowCount = maxPosition.row - 1;
  }
  if (maxPosition?.column) {
    labwareColCount = maxPosition.column - 1;
  }

  let normalizedRow = row;
  let normalizedColumn = column;

  let adjustedRowTipCount = rowTipCount;
  let adjustedColumnTipCount = columnTipCount;

  if ((is384 || isIFC) && (!noSkip || skipRow) && !isSpearBio) {
    adjustedRowTipCount = 2 * rowTipCount - 1;
    if (is384 && adjustedRowTipCount > labware.rowCount) {
      adjustedRowTipCount = rowTipCount;
    }
    normalizedRow = rowTipCount === 8 && !skipRow ? 1 : normalizedRow;
  }
  if ((is384 || isIFC) && (!noSkip || skipColumn) && !isSpearBio) {
    adjustedColumnTipCount = 2 * columnTipCount - 1;
    if (is384 && adjustedColumnTipCount > labware.colCount) {
      adjustedColumnTipCount = columnTipCount;
    }
    normalizedColumn = columnTipCount === 12 && !skipColumn ? 1 : normalizedColumn;
  }

  if (normalizedRow + adjustedRowTipCount > labwareRowCount && labwareRowCount > 1) {
    normalizedRow =
      labwareRowCount -
      adjustedRowTipCount +
      (adjustedRowTipCount === 1 ||
      normalizedRow % 2 === 0 ||
      !(is384 || isIFC) ||
      noSkip ||
      labwareRowCount - adjustedRowTipCount === 0 ||
      isSpearBio
        ? 1
        : 0);
  }

  if (normalizedColumn + adjustedColumnTipCount > labwareColCount && labwareColCount > 1) {
    normalizedColumn =
      labwareColCount -
      adjustedColumnTipCount +
      (adjustedColumnTipCount === 1 ||
      normalizedColumn % 2 === 0 ||
      !(is384 || isIFC) ||
      noSkip ||
      labwareColCount - adjustedColumnTipCount === 0 ||
      isSpearBio
        ? 1
        : 0);
  }

  if (
    normalizedRow <= minPosition?.row &&
    labwareRowCount > 1 &&
    minPosition.row + adjustedRowTipCount <= labwareRowCount
  ) {
    normalizedRow = minPosition.row + 1;
  }

  if (
    normalizedColumn <= minPosition?.column &&
    labwareColCount > 1 &&
    minPosition.column + adjustedColumnTipCount <= labwareColCount
  ) {
    normalizedColumn = minPosition.column + 1;
  }

  if (tasklist.length) {
    const relatedTasks = tasklist.filter((task) => {
      let flag = false;
      const taskRow = convertRowToNumeric(task.row);
      let taskRowTipCount = task.rowTipCount;
      let taskColumnTipCount = task.columnTipCount;

      if ((is384 || isIFC) && (!noSkip || skipRow)) {
        taskRowTipCount = 2 * task.rowTipCount - 1;
      }
      if ((is384 || isIFC) && (!noSkip || skipColumn)) {
        taskColumnTipCount = 2 * task.columnTipCount - 1;
      }

      if (
        column >= task.column &&
        column <= task.column + taskColumnTipCount - 1 &&
        row >= taskRow &&
        row <= taskRow + taskRowTipCount - 1
      ) {
        flag = true;
      }

      return flag;
    });

    for (let i = 0; i < relatedTasks.length; i += 1) {
      const task = relatedTasks[i];
      const rowTask = convertRowToNumeric(task.row);
      const columnTask = task.column;

      const rowDiff = Math.abs(row - rowTask);
      const columnDiff = Math.abs(column - columnTask);

      const isSameQuadrant = rowDiff % 2 === 0 && columnDiff % 2 === 0;

      if (
        (!(is384 || isIFC) || isSameQuadrant) &&
        row >= rowTask &&
        row < rowTask + adjustedRowTipCount &&
        column >= columnTask &&
        column < columnTask + adjustedColumnTipCount
      ) {
        normalizedRow = rowTask;
        normalizedColumn = columnTask;
      } else if (
        (!(is384 || isIFC) || isSameQuadrant) &&
        adjustedColumnTipCount === 1 &&
        row >= rowTask - adjustedRowTipCount &&
        row < rowTask
      ) {
        normalizedRow = rowTask - adjustedRowTipCount - ((is384 || isIFC) && rowDiff % 2 === 0 ? 1 : 0);
      } else if (
        (!(is384 || isIFC) || isSameQuadrant) &&
        adjustedRowTipCount === 1 &&
        column >= columnTask - adjustedColumnTipCount &&
        column < columnTask
      ) {
        normalizedColumn = columnTask - adjustedColumnTipCount - ((is384 || isIFC) && columnDiff % 2 === 0 ? 1 : 0);
      }

      if (isSourceTrough) {
        if (adjustedRowTipCount === 1 && adjustedColumnTipCount > 1) {
          normalizedColumn = columnTask;
        }

        if (adjustedColumnTipCount === 1 && adjustedRowTipCount > 1) {
          normalizedRow = rowTask;
        }
      }

      if (is384) {
        if (!skipRow && rowTipCount === 8) {
          normalizedRow = 1;
        }
        if (!skipColumn && columnTipCount === 12) {
          normalizedColumn = 1;
        }
      }
    }
  }

  if (normalizedRow < 1) {
    normalizedRow = Math.abs(normalizedRow % 2) === 0 ? 2 : 1;
  }

  if (normalizedColumn < 1) {
    normalizedColumn = Math.abs(normalizedColumn % 2) === 0 ? 2 : 1;
  }

  return [normalizedRow, normalizedColumn];
};

export const normalizeHighlightedWellSameStartingWell = (row, column, tipCount, referenceTasks) => {
  if (!referenceTasks.length) {
    return [row, column];
  }

  if (tipCount.row > 1) {
    return [convertRowToNumeric(referenceTasks[0].row), column];
  }

  if (tipCount.column > 1) {
    return [row, referenceTasks[0].column];
  }

  if (tipCount.row === 1 && tipCount.column === 1) {
    if (!referenceTasks[1]) {
      if (row === referenceTasks[0].row || column === referenceTasks[0].column) {
        return [row, column];
      }
      return [convertRowToNumeric(referenceTasks[0].row), column];
    }

    if (referenceTasks[0].row === referenceTasks[1].row) {
      return [convertRowToNumeric(referenceTasks[0].row), column];
    }

    return [row, referenceTasks[0].column];
  }

  return [row, column];
};

export const normalizeSwipingInfo = (swipingInfo, forceSkip, isFullQuadrant, isQuadrantSelection = false) => {
  const {tipCount, swipingStart, swipingEnd, swipingSize, swipingDirectionY, swipingDirectionX, labware} = swipingInfo;

  if (!swipingEnd) {
    return [swipingStart, swipingEnd];
  }

  const newTipCount = {
    row: tipCount.row ?? 1,
    column: tipCount.column ?? 1,
  };

  const newSwipingStart = {
    row: Math.min(swipingStart.row, swipingEnd.row),
    column: Math.min(swipingStart.column, swipingEnd.column),
  };

  const newSwipingEnd = {
    row: Math.max(swipingStart.row, swipingEnd.row),
    column: Math.max(swipingStart.column, swipingEnd.column),
  };

  const newSwipingSize = {
    row: swipingSize.row,
    column: swipingSize.column,
  };

  const skipWell = {
    row: 0,
    column: 0,
  };

  const is384 = isLabware384(labware) || isQuadrantSelection;
  const {isSpearBio} = labware;

  if (is384) {
    if (forceSkip === SKIP_WELL_MODE.ALL || !forceSkip) {
      skipWell.row = 1;
      skipWell.column = 1;
    } else if (forceSkip === SKIP_WELL_MODE.ROW) {
      skipWell.row = 1;
      newSwipingSize.column *= newTipCount.column > 1 ? 2 : 1;
    } else if (forceSkip === SKIP_WELL_MODE.COLUMN) {
      skipWell.column = 1;
      newSwipingSize.row *= newTipCount.row > 1 ? 2 : 1;
    } else {
      skipWell.row = 0;
      skipWell.column = 0;
      newSwipingSize.row *= newTipCount.row > 1 ? 2 : 1;
      newSwipingSize.column *= newTipCount.column > 1 ? 2 : 1;
    }

    if (swipingSize.row === 16 && skipWell.row === 0) {
      newSwipingStart.row = 1;
    }
    if (swipingSize.column === 24 && skipWell.column === 0) {
      newSwipingStart.column = 1;
    }
  }

  if (isSpearBio) {
    const isOnSBTrough = isOnSpearBioTrough(newSwipingStart.column, newSwipingSize.column);

    if (isOnSBTrough) {
      // assume that SB trough is a column-based trough labware
      // set the start row position to the last swiped row
      newSwipingStart.row = swipingEnd.row;
      newSwipingEnd.row = swipingEnd.row;
      newSwipingSize.row = 1;

      return [newSwipingStart, newSwipingEnd, newSwipingSize, skipWell];
    }
  }

  // swiping to right
  if (swipingDirectionX === SWIPING_DIRECTION_X.RIGHT) {
    if (is384) {
      if (!(newTipCount.row === 1 && skipWell.row === 0)) {
        if (newTipCount.row > 1) {
          newSwipingEnd.row = swipingStart.row + (newTipCount.row - 1) * 2;
        }

        if (newSwipingEnd.row > labware.rowCount) {
          newSwipingEnd.row -= newTipCount.row + (newTipCount.row % 2);
        }

        const rowSize = Math.ceil((newSwipingEnd.row - newSwipingStart.row + 1) / 2);
        newSwipingSize.row = Math.ceil(rowSize / newTipCount.row) * newTipCount.row;
        newSwipingStart.row = newSwipingEnd.row - (newSwipingSize.row - 1) * 2;

        // prevent swiping beyond the right side of labware
        if (newSwipingStart.row < 1) {
          newSwipingStart.row += newTipCount.row * 2;
          newSwipingSize.row -= newTipCount.row;
        }

        // normalize if shift is pressed
        if (skipWell.row === 0) {
          newSwipingSize.row *= 2;
          newSwipingEnd.row += 1;
          if (newSwipingEnd.row > labware.rowCount) {
            newSwipingStart.row -= 1;
            newSwipingEnd.row = labware.rowCount;
          }
        }
      }

      if (isFullQuadrant && skipWell.row === 0 && newSwipingStart.row % 2 === newSwipingEnd.row % 2) {
        newSwipingStart.row -= 1;
        newSwipingSize.row += 1;
      }
    } else {
      newSwipingEnd.row = swipingStart.row + newTipCount.row - 1;
      newSwipingSize.row = Math.ceil((newSwipingEnd.row - newSwipingStart.row + 1) / newTipCount.row) * newTipCount.row;
      newSwipingStart.row = newSwipingEnd.row - newSwipingSize.row + 1;

      // prevent swiping beyond the right side of labware
      if (newSwipingStart.row < 1) {
        newSwipingStart.row += newTipCount.row;
        newSwipingSize.row -= newTipCount.row;
      }
    }
  }

  // swiping to left
  if (swipingDirectionX === SWIPING_DIRECTION_X.LEFT) {
    if (is384) {
      if (!(newTipCount.row === 1 && skipWell.row === 0)) {
        const rowSize = Math.ceil((newSwipingEnd.row - newSwipingStart.row + 1) / 2);
        newSwipingSize.row = Math.ceil(rowSize / newTipCount.row) * newTipCount.row;

        if (skipWell.row === 0) {
          newSwipingSize.row *= 2;
        }

        // prevent swiping beyond the left side of labware
        newSwipingEnd.row = newSwipingStart.row + (newSwipingSize.row - 1) * (skipWell.row === 0 ? 1 : 2);
        if (newSwipingEnd.row > labware.rowCount) {
          newSwipingSize.row -= newTipCount.row * (skipWell.row === 0 ? 2 : 1);
          newSwipingEnd.row -= newTipCount.row * (skipWell.row === 0 ? 2 : 1);
        }
      }

      if (isFullQuadrant && skipWell.row === 0 && newSwipingStart.row % 2 === newSwipingEnd.row % 2) {
        newSwipingEnd.row += 1;
        newSwipingSize.row += 1;
      }
    } else {
      // prevent swiping beyond the left side of labware
      const swipingSizeTempRow =
        Math.ceil((newSwipingEnd.row - newSwipingStart.row + 1) / newTipCount.row) * newTipCount.row;
      const swipingEndTempRow = newSwipingStart.row + swipingSizeTempRow - 1;
      if (swipingEndTempRow > labware.rowCount) {
        newSwipingSize.row = swipingSizeTempRow - newTipCount.row;
        newSwipingEnd.row = swipingEndTempRow - newTipCount.row;
      }
    }
  }

  // swiping to up
  if (swipingDirectionY === SWIPING_DIRECTION_Y.UP) {
    if (is384) {
      if (!(newTipCount.column === 1 && skipWell.column === 0)) {
        if (newTipCount.column > 1) {
          newSwipingEnd.column = swipingStart.column + (newTipCount.column - 1) * 2;
        }

        if (newSwipingEnd.column > labware.colCount) {
          newSwipingEnd.column -= newTipCount.column + (newTipCount.column % 2);
        }

        const columnSize = Math.ceil((newSwipingEnd.column - newSwipingStart.column + 1) / 2);
        newSwipingSize.column = Math.ceil(columnSize / newTipCount.column) * newTipCount.column;
        newSwipingStart.column = newSwipingEnd.column - (newSwipingSize.column - 1) * 2;

        // prevent swiping beyond the top side of labware
        if (newSwipingStart.column < 1) {
          newSwipingStart.column += newTipCount.column * 2;
          newSwipingSize.column -= newTipCount.column;
        }

        // normalize if shift is pressed
        if (skipWell.column === 0) {
          newSwipingSize.column *= 2;
          newSwipingEnd.column += 1;
          if (newSwipingEnd.column > labware.colCount) {
            newSwipingStart.column -= 1;
            newSwipingEnd.column = labware.colCount;
          }
        }
      }

      if (isFullQuadrant && skipWell.column === 0 && newSwipingStart.column % 2 === newSwipingEnd.column % 2) {
        newSwipingStart.column -= 1;
        newSwipingSize.column += 1;
      }
    } else {
      newSwipingEnd.column = swipingStart.column + newTipCount.column - 1;
      newSwipingSize.column =
        Math.ceil((newSwipingEnd.column - newSwipingStart.column + 1) / newTipCount.column) * newTipCount.column;
      newSwipingStart.column = newSwipingEnd.column - newSwipingSize.column + 1;

      // prevent swiping beyond the top side of labware
      if (newSwipingStart.column < 1) {
        newSwipingStart.column += newTipCount.column;
        newSwipingSize.column -= newTipCount.column;
      }
    }
  }

  // swiping to down
  if (swipingDirectionY === SWIPING_DIRECTION_Y.DOWN) {
    if (is384) {
      if (!(newTipCount.column === 1 && skipWell.column === 0)) {
        const columnSize = Math.ceil((newSwipingEnd.column - newSwipingStart.column + 1) / 2);
        newSwipingSize.column = Math.ceil(columnSize / newTipCount.column) * newTipCount.column;

        if (skipWell.column === 0) {
          newSwipingSize.column *= 2;
        }

        // prevent swiping beyond the bottom side of labware
        newSwipingEnd.column = newSwipingStart.column + (newSwipingSize.column - 1) * (skipWell.row === 0 ? 1 : 2);
        if (newSwipingEnd.column > labware.colCount) {
          newSwipingSize.column -= newTipCount.column * (skipWell.column === 0 ? 2 : 1);
          newSwipingEnd.column -= newTipCount.column * (skipWell.column === 0 ? 2 : 1);
        }
      }

      if (isFullQuadrant && skipWell.column === 0 && newSwipingStart.column % 2 === newSwipingEnd.column % 2) {
        newSwipingEnd.column += 1;
        newSwipingSize.column += 1;
      }
    } else {
      // prevent swiping beyond the bottom side of labware
      const swipingSizeTempColumn =
        Math.ceil((newSwipingEnd.column - newSwipingStart.column + 1) / newTipCount.column) * newTipCount.column;
      const swipingStartTempColumn = newSwipingStart.column + swipingSizeTempColumn - 1;
      if (swipingStartTempColumn > labware.colCount) {
        newSwipingSize.column = swipingSizeTempColumn - newTipCount.column;
        newSwipingEnd.column = swipingStartTempColumn - newTipCount.column;
      }
    }
  }

  return [newSwipingStart, newSwipingEnd, newSwipingSize, skipWell];
};

export const initSwipingInfo = (row, column, labware, isRestricted = false) => ({
  swipingStart: {
    row,
    column,
  },
  labware,
  isRestricted,
});

export const updateSwipingInfo = (
  swipingInfo,
  tipCount,
  swipingStart,
  swipingEnd,
  swipingDirectionY,
  swipingDirectionX,
  tasks,
  strict = false,
  mustHaveSameStart = false,
  innerMode = false,
  twoDimensionalSwiping = true,
) => {
  let swipingSizeRow = Math.abs(swipingStart.row - swipingEnd.row) + 1;
  let swipingSizeColumn = Math.abs(swipingStart.column - swipingEnd.column) + 1;

  if (!tipCount.row || !tipCount.column || (tipCount.row === 1 && tipCount.column === 1)) {
    // this is the logic for single well swiping behavior
    if (tipCount.row === 1 && tipCount.column === 1 && tasks.length) {
      const task1 = tasks[0];

      if (tasks.length === 1) {
        const labwareID = swipingInfo.labware.LabwareID;
        if (mustHaveSameStart) {
          if (
            ((swipingDirectionX && task1.column !== swipingStart.column) ||
              (swipingDirectionY && convertRowToNumeric(task1.row) !== swipingStart.row)) &&
            labwareID === task1.labwareID
          ) {
            return null;
          }
        }
      } else if (tasks.length > 1) {
        if (innerMode) {
          for (let i = 0, ii = tasks.length; i < ii; i += 1) {
            const task = tasks[i];
            if (task.column === swipingStart.column && convertRowToNumeric(task.row) === swipingStart.row) {
              return null;
            }
          }
        }
      }
    }

    if (swipingDirectionY && swipingDirectionX) {
      swipingEnd.row = strict ? swipingStart.row : swipingEnd.row;
      swipingEnd.column = strict ? swipingStart.column : swipingEnd.column;
    } else if (swipingDirectionY) {
      swipingSizeRow = 1;
      swipingEnd.row = strict ? swipingStart.row : swipingEnd.row;
    } else if (swipingDirectionX) {
      swipingSizeColumn = 1;
      swipingEnd.column = strict ? swipingStart.column : swipingEnd.column;
    } else {
      swipingSizeRow = 1;
      swipingSizeColumn = 1;
      swipingEnd.row = strict ? swipingStart.row : swipingEnd.row;
      swipingEnd.column = strict ? swipingStart.column : swipingEnd.column;
    }
  } else if (
    (tipCount.column > 1 && (swipingDirectionY || (!swipingDirectionY && !swipingDirectionX))) ||
    (tipCount.row > 1 && (swipingDirectionX || (!swipingDirectionY && !swipingDirectionX)))
  ) {
    // 2 Dimensional Swiping
    const strictRowSize = !twoDimensionalSwiping && tipCount.column === 1;
    const strictColumnSize = !twoDimensionalSwiping && tipCount.row === 1;

    swipingSizeRow = strictRowSize ? tipCount.row : Math.ceil(swipingSizeRow / tipCount.row) * tipCount.row;
    swipingSizeColumn = strictColumnSize
      ? tipCount.column
      : Math.ceil(swipingSizeColumn / tipCount.column) * tipCount.column;

    swipingEnd.column = strict || strictColumnSize ? swipingStart.column + swipingSizeColumn - 1 : swipingEnd.column;
    swipingEnd.row = strict || strictRowSize ? swipingStart.row + swipingSizeRow - 1 : swipingEnd.row;
  } else if (tipCount.row > tipCount.column && (swipingDirectionY || (!swipingDirectionY && !swipingDirectionX))) {
    swipingSizeRow = tipCount.row;
    swipingEnd.row = strict ? swipingStart.row + swipingSizeRow - 1 : swipingEnd.row;
  } else if (tipCount.row < tipCount.column && (swipingDirectionX || (!swipingDirectionY && !swipingDirectionX))) {
    swipingSizeColumn = tipCount.column;
    swipingEnd.column = strict ? swipingStart.column + swipingSizeColumn - 1 : swipingEnd.column;
  } else if (tipCount.row > tipCount.column && (swipingDirectionX || (!swipingDirectionY && !swipingDirectionX))) {
    swipingSizeRow = tipCount.row;
    swipingEnd.row = strict ? swipingStart.row + swipingSizeRow - 1 : swipingEnd.row;
  } else if (tipCount.row < tipCount.column && (swipingDirectionY || (!swipingDirectionY && !swipingDirectionX))) {
    swipingSizeColumn = tipCount.column;
    swipingEnd.column = strict ? swipingStart.column + swipingSizeColumn - 1 : swipingEnd.column;
  } else {
    return null;
  }

  return {
    ...swipingInfo,
    tipCount,
    swipingStart,
    swipingEnd,
    swipingSize: {
      row: swipingSizeRow,
      column: swipingSizeColumn,
    },
    swipingDirectionY,
    swipingDirectionX,
    isRestricted: false,
  };
};

export const validateSwipingInfo = (swipingInfo, tasks, noSkip = false) => {
  const [normalizedSwipingStart, normalizedSwipingEnd] = normalizeSwipingInfo(swipingInfo, noSkip);
  if (normalizedSwipingStart.row === normalizedSwipingEnd.row) {
    for (let j = 0, jj = tasks.length; j < jj; j += 1) {
      const task = tasks[j];
      if (convertRowToNumeric(task.row) === normalizedSwipingStart.row) {
        return false;
      }
    }
    return true;
  }

  if (normalizedSwipingStart.column === normalizedSwipingEnd.column) {
    for (let j = 0, jj = tasks.length; j < jj; j += 1) {
      const task = tasks[j];
      if (task.column === normalizedSwipingStart.column) {
        return false;
      }
    }
  }
  return true;
};

// check if hovered well(s) is already taken (there is task with those well(s))
export const getExistingTaskFromHighlightedWell = (
  row,
  column,
  tipCount,
  tasks,
  taskSize = null,
  innerMode = false,
  taskPattern = undefined,
) => {
  // innerMode === true : return task only when user highlighted exactly on the well (only applied on single well)
  // innerMode === false : return task when user highlighted on the well with same column/row (only applied on single well)
  // when tasks is using single tips, naturally this function will return task that have same row/column, doesnt matter if the tasks pattern is column base/row base
  // taskPattern = NO_BASE, to make this function considering the pattern (col/row base) based on tasks
  // taskPattern = ROW_BASE, to make this function act as if the task is row base (ignoring the parameter tasks)
  // taskPattern = COLUMN_BASE, to make this function act as if the task is column base (ignoring the parameter tasks)
  // emtpy tasks or no well(s) are taken
  if (!tipCount.row || !tasks.length) {
    return null;
  }

  let pos;
  let isRowBased = true;
  if (tipCount.row === 1 && tipCount.column === 1 && !(taskSize?.row !== 1 && taskSize?.column !== 1)) {
    // single well
    // if there is only 1 task
    // it means direction still can be determined (could be row / column based)
    if (tasks.length === 1 && !taskPattern) {
      const task = tasks[0];

      const taskPosRow = convertRowToNumeric(task.row);
      const taskPosColumn = task.column;
      const {rowTipCount: taskRowTipCount, columnTipCount: taskColumnTipCount} = task;
      if (taskRowTipCount > 1 || taskColumnTipCount > 1) {
        if (taskRowTipCount > 1 && column === taskPosColumn) {
          return task;
        }

        if (taskColumnTipCount > 1 && row === taskPosRow) {
          return task;
        }
        return null;
      }

      if (innerMode && taskPosRow === row && taskPosColumn === column) {
        return task;
      }

      if (!innerMode && (taskPosRow === row || taskPosColumn === column)) {
        return task;
      }
    } else if (tasks.length === 1 && taskPattern === TASK_PATTERN.ROW_BASE) {
      pos = column;
      isRowBased = false;
    } else if (tasks.length === 1 && taskPattern === TASK_PATTERN.COLUMN_BASE) {
      pos = row;
    } else if (tasks.length === 1 && taskPattern === TASK_PATTERN.NO_BASE) {
      return null;
    } else {
      if (taskSize?.row === 1 && taskSize?.column === 1 && !taskPattern) {
        // if single well and not row/column based
        for (let i = 0, ii = tasks.length; i < ii; i += 1) {
          const task = tasks[i];
          if (!innerMode && (task.column === column || convertRowToNumeric(task.row) === row)) {
            return task;
          }

          if (innerMode && convertRowToNumeric(task.row) === row && task.column === column) {
            return task;
          }
        }
        return null;
      }
      if (taskPattern === TASK_PATTERN.ROW_BASE) {
        pos = column;
        isRowBased = false;
      } else if (taskPattern === TASK_PATTERN.COLUMN_BASE) {
        pos = row;
      } else {
        const task1 = tasks[0];
        const task2 = tasks[1];

        const task1PosRow = convertRowToNumeric(task1.row);
        const task2PosRow = convertRowToNumeric(task2.row);

        if (task1PosRow === task2PosRow) {
          // row based
          pos = column;
          isRowBased = false;
        } else {
          // column based
          pos = row;
        }
      }
    }
  } else if (tipCount.row === 1) {
    // column based
    pos = row;
  } else if (tipCount.column === 1) {
    // row based
    pos = column;
    isRowBased = false;
  } else {
    const sampleTask = tasks[0];
    if (sampleTask?.rowTipCount === 1 && sampleTask?.columnTipCount > 1) {
      // column based
      pos = row;
    } else if (sampleTask?.columnTipCount === 1 && sampleTask?.rowTipCount > 1) {
      // row based
      pos = column;
      isRowBased = false;
    } else {
      for (let i = 0, ii = tasks.length; i < ii; i += 1) {
        const task = tasks[i];
        if (!innerMode && (task.column === column || convertRowToNumeric(task.row) === row)) {
          return task;
        }

        if (innerMode && convertRowToNumeric(task.row) === row && task.column === column) {
          return task;
        }
      }
      return null;
    }
  }

  if (innerMode) {
    for (let i = 0, ii = tasks.length; i < ii; i += 1) {
      const task = tasks[i];
      if (convertRowToNumeric(task.row) === row && task.column === column) {
        return task;
      }
    }
    return null;
  }

  for (let i = 0, ii = tasks.length; i < ii; i += 1) {
    const task = tasks[i];
    const taskPos = isRowBased ? convertRowToNumeric(task.row) : task.column;

    if (taskPos !== pos) {
      continue;
    }

    return task;
  }

  return null;
};

export const generateRowColumnsListFromSwipingInfo = (
  swipingInfo,
  forceSkip,
  noSkip = false,
  tip = {},
  optimizeMode = null,
  forceToSplit = false,
  forceSingleTask = false,
) => {
  const {tipCount, swipingDirectionY, swipingDirectionX, labware} = swipingInfo;
  const normalizedSwipingInfo = normalizeSwipingInfo(swipingInfo, forceSkip, noSkip);
  const [normalizedSwipingStart] = normalizedSwipingInfo;
  let [, normalizedSwipingEnd, normalizedSwipingSize, skipWell] = normalizedSwipingInfo;

  const is384 = isLabware384(labware);

  let stepRowCount = 0;
  let stepColCount = 0;

  if (!normalizedSwipingEnd) {
    normalizedSwipingEnd = normalizedSwipingStart;
  }

  if (!normalizedSwipingSize) {
    const rowSize = Math.abs(normalizedSwipingEnd.row - normalizedSwipingStart.row) + 1;
    const colSize = Math.abs(normalizedSwipingEnd.column - normalizedSwipingStart.column) + 1;
    normalizedSwipingSize = {row: rowSize, column: colSize};
  }

  if (!skipWell) {
    skipWell = {row: null, column: null};
  }

  let columnTipCount = !tip.column ? normalizedSwipingSize.column : tip.column;
  let rowTipCount = !tip.row ? normalizedSwipingSize.row : tip.row;

  let startingRow = normalizedSwipingStart.row;
  let startingColumn = normalizedSwipingStart.column;

  skipWell.row = skipWell?.row > 0 ? skipWell.row : 0;
  skipWell.column = skipWell?.column > 0 ? skipWell.column : 0;

  if (
    ((!tipCount?.row ||
      !tipCount?.column ||
      (normalizedSwipingStart.row === normalizedSwipingEnd.row &&
        normalizedSwipingStart.column === normalizedSwipingEnd.column)) &&
      !forceToSplit) ||
    forceSingleTask
  ) {
    if (is384 && optimizeMode) {
      const startingRowColumnList = [];
      for (let i = 0; i < normalizedSwipingSize.row; i += 1) {
        for (let j = 0; j < normalizedSwipingSize.column; j += 1) {
          const quadrant = getQuadrant(i + 1, j + 1);
          let newStartRow = startingRow;
          let newStartColumn = startingColumn;
          const newTipCount = {
            row: Math.ceil(rowTipCount / 2),
            column: Math.ceil(columnTipCount / 2),
          };

          if (optimizeMode === OPTIMIZE_MODE.ALL) {
            switch (quadrant) {
              case 2:
                newStartColumn = startingColumn + 1;
                newTipCount.column =
                  normalizedSwipingSize.column % 2 === 0
                    ? Math.ceil(columnTipCount / 2)
                    : Math.ceil(normalizedSwipingSize.column / 2) -
                      (normalizedSwipingSize.column % 2) * (newStartColumn - startingColumn);
                break;
              case 3:
                newStartRow = startingRow + 1;
                newStartColumn = startingColumn + 1;
                newTipCount.row =
                  normalizedSwipingSize.row % 2 === 0
                    ? Math.ceil(rowTipCount / 2)
                    : Math.ceil(normalizedSwipingSize.row / 2) -
                      (normalizedSwipingSize.row % 2) * (newStartRow - startingRow);
                newTipCount.column =
                  normalizedSwipingSize.column % 2 === 0
                    ? Math.ceil(columnTipCount / 2)
                    : Math.ceil(normalizedSwipingSize.column / 2) -
                      (normalizedSwipingSize.column % 2) * (newStartColumn - startingColumn);
                break;
              case 4:
                newStartRow = startingRow + 1;
                newTipCount.row =
                  normalizedSwipingSize.row % 2 === 0
                    ? Math.ceil(rowTipCount / 2)
                    : Math.ceil(normalizedSwipingSize.row / 2) -
                      (normalizedSwipingSize.row % 2) * (newStartRow - startingRow);
                break;
              default:
                break;
            }
          } else if (optimizeMode === OPTIMIZE_MODE.COLUMN && j < 2) {
            newStartRow = startingRow + i + i * skipWell.row;
            newStartColumn = startingColumn + j + j * skipWell.row;
            newTipCount.row = 1;
            newTipCount.column =
              Math.ceil(normalizedSwipingSize.column / 2) -
              (normalizedSwipingSize.column % 2) * (newStartColumn - startingColumn);
          } else if (optimizeMode === OPTIMIZE_MODE.ROW && i < 2) {
            newStartRow = startingRow + i + i * skipWell.row;
            newStartColumn = startingColumn + j + j * skipWell.row;
            newTipCount.row =
              Math.ceil(normalizedSwipingSize.row / 2) - (normalizedSwipingSize.row % 2) * (newStartRow - startingRow);
            newTipCount.column = 1;
          }

          const sameRowColumnStart = startingRowColumnList.find(
            (el) => el.row === newStartRow && el.column === newStartColumn,
          );

          if (!sameRowColumnStart) {
            startingRowColumnList.push({row: newStartRow, column: newStartColumn, tipCount: newTipCount});
          }
        }
      }

      return {
        startingRowColumnList,
        tipCount: {
          row: Math.ceil(rowTipCount / 2),
          column: Math.ceil(columnTipCount / 2),
        },
      };
    }
    return {
      startingRowColumnList: [
        {
          row: startingRow,
          column: startingColumn,
        },
      ],
      tipCount: {
        row: rowTipCount,
        column: columnTipCount,
      },
    };
  }

  let startingRowIncrementor = 0;
  let startingColumnIncrementor = 0;

  if (rowTipCount > 1 && !swipingDirectionX && is384) {
    stepRowCount = skipWell.row ? 1 : 2;
  }
  if (columnTipCount > 1 && !swipingDirectionY && is384) {
    stepColCount = skipWell.column ? 1 : 2;
  }
  if (swipingDirectionY === SWIPING_DIRECTION_Y.UP) {
    columnTipCount = tip?.column ?? 1;
    if (columnTipCount === 1) {
      startingColumn = normalizedSwipingEnd.column;
    } else {
      startingColumn = normalizedSwipingEnd.column - (columnTipCount - 1) * (is384 ? 2 : 1);
    }
    stepColCount = normalizedSwipingSize.column / columnTipCount;
    startingColumnIncrementor = (skipWell.column + 1) * -1 * columnTipCount;
  } else if (swipingDirectionY === SWIPING_DIRECTION_Y.DOWN) {
    columnTipCount = tip?.column ?? 1;
    stepColCount = normalizedSwipingSize.column / columnTipCount;
    startingColumnIncrementor = columnTipCount * (skipWell.column === 1 ? 2 : 1);
  }

  if (swipingDirectionX === SWIPING_DIRECTION_X.LEFT) {
    rowTipCount = tip?.row ?? 1;
    stepRowCount = normalizedSwipingSize.row / rowTipCount;
    startingRowIncrementor = rowTipCount * (skipWell.row === 1 ? 2 : 1);
  } else if (swipingDirectionX === SWIPING_DIRECTION_X.RIGHT) {
    rowTipCount = tip?.row ?? 1;
    if (rowTipCount === 1) {
      startingRow = normalizedSwipingEnd.row;
    } else {
      startingRow = normalizedSwipingEnd.row - (rowTipCount - 1) * (is384 ? 2 : 1);
    }
    stepRowCount = normalizedSwipingSize.row / rowTipCount;
    startingRowIncrementor = (skipWell.row + 1) * -1 * rowTipCount;
  }

  stepRowCount = stepRowCount < 1 && stepColCount > 0 ? 1 : stepRowCount;
  stepColCount = stepColCount < 1 && stepRowCount > 0 ? 1 : stepColCount;

  const startingRowColumnList = [];
  let column;
  let row = startingRow;
  for (let i = 0; i < stepRowCount; i += 1) {
    column = startingColumn;
    for (let j = 0; j < stepColCount; j += 1) {
      startingRowColumnList.push({
        row,
        column,
      });
      if (is384 && skipWell.column === 0 && columnTipCount > 1) {
        const sign = startingColumnIncrementor >= 0 ? 1 : -1;
        if ((j + 1) % 2 === 1) {
          column += sign * 1;
        } else {
          column += startingColumnIncrementor * 2 - 1 * sign;
        }
      } else {
        column += startingColumnIncrementor;
      }
    }
    if (is384 && skipWell.row === 0 && rowTipCount > 1) {
      const sign = startingRowIncrementor >= 0 ? 1 : -1;
      if ((i + 1) % 2 === 1) {
        row += sign * 1;
      } else {
        row += startingRowIncrementor * 2 - 1 * sign;
      }
    } else {
      row += startingRowIncrementor;
    }
  }

  return {
    startingRowColumnList,
    tipCount: {
      row: rowTipCount,
      column: columnTipCount,
    },
  };
};

export const normalizeRowColoumnList = ({
  rowColumnList,
  taskDispenses,
  labwareState = {},
  isNeedOptimize = false,
  is384 = false,
  skipWellMode = null,
}) => {
  const {startingRowColumnList, tipCount: newTipCount} = rowColumnList;
  let listToAdd = Array.from(startingRowColumnList);

  const listToRemove = [];
  const newDispenseWell = [];
  const existingDispenseWell = [];
  const filteredOutItemIndex = [];
  let existingRowColumn = {};

  const [skipRow, skipColumn] = getSkipRowColumn(skipWellMode);

  startingRowColumnList.forEach((item) => {
    const maxRow = item?.tipCount?.row ?? newTipCount.row;
    const maxColumn = item?.tipCount?.column ?? newTipCount.column;
    const eachNewDispenseWell = [];
    for (let i = 0; i < maxRow; i += 1) {
      for (let j = 0; j < maxColumn; j += 1) {
        const row = item.row + (skipRow || is384 ? 2 * i : i);
        const column = item.column + (skipColumn || is384 ? 2 * j : j);
        eachNewDispenseWell.push(`${row}-${column}`);
      }
    }
    newDispenseWell.push(eachNewDispenseWell);
  });

  taskDispenses.forEach((task) => {
    const rowTask = convertRowToNumeric(task.row);
    const {column: columnTask, rowTipCount, columnTipCount, labwareID} = task;

    const existingDispenseTaskWell = [];
    let key;

    for (let i = 0; i < rowTipCount; i += 1) {
      for (let j = 0; j < columnTipCount; j += 1) {
        const row = rowTask + (is384 ? 2 * i : i);
        const column = columnTask + (is384 ? 2 * j : j);
        existingDispenseTaskWell.push(`${row}-${column}`);

        if (isNeedOptimize) {
          if (newTipCount.column === 1) {
            key = `${rowTask}-${column}`;
          } else if (newTipCount.row === 1) {
            key = `${row}-${columnTask}`;
          }
          if (!existingRowColumn[key]) {
            existingRowColumn[key] = [];
          }
          existingRowColumn[key].push(`${row}-${column}`);
        }
      }
    }

    const intersectRowColumn = existingDispenseTaskWell.filter((well) => {
      const taskLabware = find(labwareState, {LabwareID: labwareID});

      if (taskLabware?.isSpearBio) {
        const isOnTrough = isOnSpearBioTrough(columnTask, columnTipCount);

        if (isOnTrough) {
          const [, column] = well.split('-');

          const pattern = new RegExp(`-${column}`);
          const isIntersected = pattern.test(newDispenseWell.flat());
          return isIntersected;
        }
      }

      return newDispenseWell.flat().includes(well);
    });

    if (intersectRowColumn.length) {
      intersectRowColumn.forEach((rowColumn) => {
        const newIndex = newDispenseWell.findIndex((wells) => wells.includes(rowColumn));

        if (!filteredOutItemIndex.includes(newIndex)) {
          filteredOutItemIndex.push(newIndex);
        }
      });
      existingDispenseWell.push(existingDispenseTaskWell);
      listToRemove.push(task.ID);
    } else {
      existingRowColumn = {};
    }
  });

  listToAdd = listToAdd.filter((el, idx) => !filteredOutItemIndex.includes(idx));

  if (isNeedOptimize) {
    let dispenseWellMinus = [];
    const filteredOutNewDispenseWell = filteredOutItemIndex.map((idx) => newDispenseWell[idx]).flat();
    if (newTipCount.row === 1 && newTipCount.column === 1) {
      const existingDispenseWellMinus = existingDispenseWell
        .flat()
        .filter((well) => !newDispenseWell.flat().includes(well));
      const filteredOutNewDispenseWellMinus = filteredOutNewDispenseWell.filter(
        (well) => !existingDispenseWell.flat().includes(well),
      );
      dispenseWellMinus = _.concat(existingDispenseWellMinus, filteredOutNewDispenseWellMinus);
    } else if (Object.keys(existingRowColumn).length) {
      dispenseWellMinus = Object.keys(existingRowColumn).filter(
        (key) => !filteredOutNewDispenseWell.some((well) => existingRowColumn[key].find((e) => well === e)),
      );
    }

    if (dispenseWellMinus.length) {
      dispenseWellMinus.forEach((el) => {
        const [rowStr, columnStr] = el.split('-');
        const row = parseInt(rowStr, 10);
        const column = parseInt(columnStr, 10);
        listToAdd.push({row, column, tipCount: newTipCount});
      });
    }
  }

  if (startingRowColumnList[0].row > startingRowColumnList[startingRowColumnList.length - 1].row) {
    listToAdd = listToAdd.sort((a, b) => b.row - a.row);
  } else if (startingRowColumnList[0].column > startingRowColumnList[startingRowColumnList.length - 1].column) {
    listToAdd = listToAdd.sort((a, b) => b.column - a.column);
  } else {
    // eslint-disable-next-line no-lonely-if
    if (newTipCount.column === 1) {
      listToAdd = listToAdd.sort((a, b) => a.column - b.column);
    } else if (newTipCount.row === 1) {
      listToAdd = listToAdd.sort((a, b) => a.row - b.row);
    }
  }

  const newRowColumnList = {
    ...rowColumnList,
    tipCount: newTipCount,
    startingRowColumnList: _.uniqWith(listToAdd, _.isEqual),
  };

  return [newRowColumnList, listToRemove];
};

export const sortTaskByWellPosition = (tasks, asc = false, rowBase = true) => {
  const compareDesc = (a, b) => {
    let aIndex = a.row.toString().padStart(3, '0') + a.column.toString().padStart(3, '0');
    let bIndex = b.row.toString().padStart(3, '0') + b.column.toString().padStart(3, '0');
    if (!rowBase) {
      aIndex = a.column.toString().padStart(3, '0') + a.row.toString().padStart(3, '0');
      bIndex = b.column.toString().padStart(3, '0') + b.row.toString().padStart(3, '0');
    }

    if (aIndex < bIndex) {
      return 1;
    }
    if (aIndex > bIndex) {
      return -1;
    }
    return 0;
  };
  const compareAsc = (a, b) => {
    let aIndex = a.row.toString().padStart(3, '0') + a.column.toString().padStart(3, '0');
    let bIndex = b.row.toString().padStart(3, '0') + b.column.toString().padStart(3, '0');
    if (!rowBase) {
      aIndex = a.column.toString().padStart(3, '0') + a.row.toString().padStart(3, '0');
      bIndex = b.column.toString().padStart(3, '0') + b.row.toString().padStart(3, '0');
    }

    if (aIndex < bIndex) {
      return -1;
    }
    if (aIndex > bIndex) {
      return 1;
    }
    return 0;
  };
  if (asc) {
    return tasks.sort(compareAsc);
  }
  return tasks.sort(compareDesc);
};

export const getTipFromTaskSize = (tasks, is384 = false, ignoreCurrentTip = false) => {
  const sortedTasks = sortTaskByWellPosition(jsonCopy(tasks));
  const firstTask = sortedTasks[0];
  const lastTask = sortedTasks[sortedTasks.length - 1];

  const firstTaskRow = Number.isInteger(firstTask.row) ? firstTask.row : convertRowToNumeric(firstTask.row);
  const lastTaskRow = Number.isInteger(lastTask.row) ? lastTask.row : convertRowToNumeric(lastTask.row);

  let rowTip = Math.abs(lastTaskRow - firstTaskRow) + 1;
  rowTip = is384 ? Math.ceil(rowTip / 2) : rowTip;
  if (!ignoreCurrentTip) rowTip *= firstTask.rowTipCount;

  let columnTip = Math.abs(lastTask.column - firstTask.column) + 1;
  columnTip = is384 ? Math.ceil(columnTip / 2) : columnTip;
  if (!ignoreCurrentTip) columnTip *= firstTask.columnTipCount;

  return {rowTip, columnTip};
};

export const getHighlightedWell = (row, column, rowTipCount, columnTipCount, skipWellMode = null) => {
  const highlightedWell = [];

  const [skipRow, skipColumn] = getSkipRowColumn(skipWellMode);

  for (let i = 0; i < rowTipCount; i += 1) {
    for (let j = 0; j < columnTipCount; j += 1) {
      highlightedWell.push(`${row + (skipRow ? 2 * i : i)}-${column + (skipColumn ? 2 * j : j)}`);
    }
  }

  return highlightedWell;
};

export const getHighlightedWellFromTask = (tasklist, skipWellMode) => {
  const highlightedWell = [];

  const [skipRow, skipColumn] = getSkipRowColumn(skipWellMode);

  if (tasklist.length) {
    tasklist.forEach((task) => {
      const {row, column, rowTipCount, columnTipCount} = task;

      for (let i = 0; i < rowTipCount; i += 1) {
        for (let j = 0; j < columnTipCount; j += 1) {
          highlightedWell.push(
            `${convertRowToNumeric(row) + (skipRow ? 2 * i : i)}-${column + (skipColumn ? 2 * j : j)}`,
          );
        }
      }
    });
  }

  return highlightedWell;
};

export const getMinMaxPositionFromTaskList = (taskList, row, column, labware, skipWellMode) => {
  const {rowCount, colCount} = labware;

  const is384 = isLabware384(labware);

  const [skipRow, skipColumn] = getSkipRowColumn(skipWellMode);

  let minPositionRow = 0;
  let minPositionColumn = 0;
  let maxPositionRow = rowCount + 1;
  let maxPositionColumn = colCount + 1;

  if (taskList.length) {
    taskList.forEach((task) => {
      const {row: rowTask, column: columnTask, rowTipCount, columnTipCount} = task;

      for (let i = 0; i < rowTipCount; i += 1) {
        for (let j = 0; j < columnTipCount; j += 1) {
          const highlightedRow = convertRowToNumeric(rowTask) + (skipRow ? 2 * i : i);
          const highlightedColumn = columnTask + (skipColumn ? 2 * j : j);
          const isSameRowQuadrant = highlightedRow % 2 === row % 2;
          const isSameColumnQuadrant = highlightedColumn % 2 === column % 2;

          if (row === highlightedRow && (!is384 || (isSameRowQuadrant && isSameColumnQuadrant))) {
            if (highlightedColumn < column && highlightedColumn > minPositionColumn) {
              minPositionColumn = highlightedColumn;
            }
            if (highlightedColumn > column && highlightedColumn < maxPositionColumn) {
              maxPositionColumn = highlightedColumn;
            }
          }

          if (column === highlightedColumn && (!is384 || (isSameRowQuadrant && isSameColumnQuadrant))) {
            if (highlightedRow < row && highlightedRow > minPositionRow) {
              minPositionRow = highlightedRow;
            }
            if (highlightedRow > row && highlightedRow < maxPositionRow) {
              maxPositionRow = highlightedRow;
            }
          }
        }
      }
    });
  }

  const minPosition = {
    row: minPositionRow ?? 0,
    column: minPositionColumn ?? 0,
  };

  const maxPosition = {
    row: maxPositionRow ?? rowCount + 1,
    column: maxPositionColumn ?? colCount + 1,
  };

  return [minPosition, maxPosition];
};

export const getTaskGroups = (
  tasks,
  labwareState,
  columnBase = true,
  forceToSplit = {row: false, column: false},
  splitBeforeGrouping = {row: false, column: false},
  maxSizePerGroup = {row: null, column: null},
) => {
  const getTaskFromLabware = (labwareID) => filterToArray(tasks, {labwareID});
  const initPattern = (t) => ({
    row: t.row,
    column: t.column,
    colLength: t.columnTipCount,
    rowLength: t.rowTipCount,
  });
  const initTaskGroup = (task, labwareId, deckId, is384Labware) => ({
    row: task.row,
    column: task.column,
    rowTipCount: task.rowLength,
    columnTipCount: task.colLength,
    labwareId,
    deckId,
    isLabware384: is384Labware,
  });
  const splitTasks = (aspirateTasks, is384) => {
    const brokenDownTasks = [];

    aspirateTasks.forEach((t) => {
      const rowTip = splitBeforeGrouping.row;
      const colTip = splitBeforeGrouping.column;
      let rowTaskCount = 1;
      let columnTaskCount = 1;
      if (rowTip < t.rowTipCount) {
        rowTaskCount = Math.floor(t.rowTipCount / rowTip);
      }
      if (colTip < t.columnTipCount) {
        columnTaskCount = Math.floor(t.columnTipCount / colTip);
      }

      const numRow = convertRowToNumeric(t.row);

      for (let c = 0; c < columnTaskCount; c += 1) {
        const newCol = t.column + c * (1 + is384) * colTip;
        for (let r = 0; r < rowTaskCount; r += 1) {
          const newRow = numRow + r * (1 + is384) * rowTip;
          brokenDownTasks.push({
            ...t,
            row: convertRowToAlphabet(newRow),
            column: newCol,
            rowTipCount: rowTip,
            columnTipCount: colTip,
          });
        }
      }
    });
    return brokenDownTasks;
  };

  const taskGroups = [];

  for (let deckId = 1; deckId <= 6; deckId += 1) {
    const labware = labwareState[deckId];
    const unsortedTaskAspirates = labware.LabwareID ? getTaskFromLabware(labware.LabwareID) : [];
    let pattern = [];
    const tempPattern = {};
    const is384 = isLabware384(labware) ? 1 : 0;
    const maxColumn = maxSizePerGroup.column ?? labware.colCount;
    const maxRow = maxSizePerGroup.row ?? labware.rowCount;

    if (forceToSplit?.row && forceToSplit?.column && unsortedTaskAspirates.length) {
      unsortedTaskAspirates.forEach((t) => {
        for (let r = 0; r < t.rowTipCount; r += 1) {
          for (let c = 0; c < t.columnTipCount; c += 1) {
            const rowNum = convertRowToNumeric(t.row) + r * (1 + is384);
            taskGroups.push({
              row: convertRowToAlphabet(rowNum),
              column: t.column + c * (1 + is384),
              rowTipCount: 1,
              columnTipCount: 1,
              labwareId: labware.LabwareID,
              deckId,
              isLabware384: is384 === 1,
            });
          }
        }
      });
    } else if (columnBase) {
      let sortedTaskAspirates = sortTaskByWellPosition(unsortedTaskAspirates, true);

      if (splitBeforeGrouping?.row && splitBeforeGrouping?.column) {
        sortedTaskAspirates = splitTasks(sortedTaskAspirates, is384);
      }

      sortedTaskAspirates.forEach((t) => {
        if (!(t.row in tempPattern)) {
          tempPattern[t.row] = [initPattern(t)];
        } else {
          let got = false;
          for (let i = 0; i < tempPattern[t.row].length; i += 1) {
            const currentGroup = tempPattern[t.row][i];
            const col = currentGroup.column;
            if (
              t.column === col + currentGroup.colLength * (1 + is384) &&
              tempPattern[t.row][i].colLength + t.columnTipCount <= maxColumn
            ) {
              tempPattern[t.row][i].colLength += t.columnTipCount;
              got = true;
              break;
            }
          }

          if (!got) {
            tempPattern[t.row].push(initPattern(t));
          }
        }
      });

      const rowKeys = Object.keys(tempPattern);
      rowKeys.forEach((key) => {
        pattern = [...pattern, ...tempPattern[key]];
      });
      const indexLib = {};
      pattern.forEach((t) => {
        if (!(t.column in indexLib)) {
          indexLib[t.column] = [taskGroups.length];
          taskGroups.push(initTaskGroup(t, labware.LabwareID, deckId, is384 === 1));
        } else {
          let got = false;
          for (let i = 0; i < indexLib[t.column].length; i += 1) {
            const currentGroup = taskGroups[indexLib[t.column][i]];
            const curRow = convertRowToNumeric(currentGroup.row);

            if (
              !forceToSplit?.row &&
              convertRowToNumeric(t.row) === curRow + currentGroup.rowTipCount * (1 + is384) &&
              t.colLength === currentGroup.columnTipCount &&
              taskGroups[indexLib[t.column][i]].rowTipCount + t.rowLength <= maxRow
            ) {
              taskGroups[indexLib[t.column][i]].rowTipCount += t.rowLength;
              got = true;
              break;
            }
          }

          if (!got) {
            indexLib[t.column].push(taskGroups.length);
            taskGroups.push(initTaskGroup(t, labware.LabwareID, deckId, is384 === 1));
          }
        }
      });
    } else {
      let sortedTaskAspirates = sortTaskByWellPosition(unsortedTaskAspirates, true, false);

      if (splitBeforeGrouping?.row && splitBeforeGrouping?.column) {
        sortedTaskAspirates = splitTasks(sortedTaskAspirates, is384);
      }

      sortedTaskAspirates.forEach((t) => {
        if (!(t.column in tempPattern)) {
          tempPattern[t.column] = [initPattern(t)];
        } else {
          let got = false;
          for (let i = 0; i < tempPattern[t.column].length; i += 1) {
            const currentGroup = tempPattern[t.column][i];
            const curRow = convertRowToNumeric(currentGroup.row);
            if (
              convertRowToNumeric(t.row) === curRow + currentGroup.rowLength * (1 + is384) &&
              tempPattern[t.column][i].rowLength + t.rowTipCount <= maxRow
            ) {
              tempPattern[t.column][i].rowLength += t.rowTipCount;
              got = true;
              break;
            }
          }

          if (!got) {
            tempPattern[t.column].push(initPattern(t));
          }
        }
      });

      const colKeys = Object.keys(tempPattern);
      colKeys.forEach((key) => {
        pattern = [...pattern, ...tempPattern[key]];
      });
      const indexLib = {};
      pattern.forEach((t) => {
        if (!(t.row in indexLib)) {
          indexLib[t.row] = [taskGroups.length];
          taskGroups.push(initTaskGroup(t, labware.LabwareID, deckId, is384 === 1));
        } else {
          let got = false;
          for (let i = 0; i < indexLib[t.row].length; i += 1) {
            const currentGroup = taskGroups[indexLib[t.row][i]];
            const col = currentGroup.column;

            if (
              !forceToSplit?.column &&
              t.column === col + currentGroup.columnTipCount * (1 + is384) &&
              t.rowLength === currentGroup.rowTipCount &&
              taskGroups[indexLib[t.row][i]].columnTipCount + t.colLength <= maxColumn
            ) {
              taskGroups[indexLib[t.row][i]].columnTipCount += t.colLength;
              got = true;
              break;
            }
          }

          if (!got) {
            indexLib[t.row].push(taskGroups.length);
            taskGroups.push(initTaskGroup(t, labware.LabwareID, deckId, is384 === 1));
          }
        }
      });
    }
  }
  return taskGroups;
};

export const getTaskGroupByTipCountAndLabware = (taskGroup, labwareState) => {
  let taskGroupByTipCount = {};
  taskGroup.forEach((task) => {
    const {labwareId: labwareID} = task;

    const groupId = `${task.rowTipCount}-${task.columnTipCount}-${labwareID}`;
    const updatedTaskGroup = taskGroupByTipCount[groupId] ?? {};
    const startingRowColumnList = updatedTaskGroup?.startingRowColumnList ?? [];

    const rowNum = convertRowToNumeric(task.row);
    const columnNum = task.column;

    const isAlreadyExistTask = startingRowColumnList.find((t) => t.row === rowNum && t.column === columnNum);
    const taskLabware = find(labwareState, {LabwareID: labwareID});

    if (!isAlreadyExistTask && rowNum <= taskLabware.rowCount && columnNum <= taskLabware.colCount) {
      startingRowColumnList.push({row: rowNum, column: columnNum});
    }

    taskGroupByTipCount = {
      ...taskGroupByTipCount,
      [groupId]: {
        startingRowColumnList,
        tipCount: {row: task.rowTipCount, column: task.columnTipCount},
        labwareID,
      },
    };
  });

  return taskGroupByTipCount;
};

export const isTasksSameQuadrant = (tasklist, is384, retrieveQuadrant = false) => {
  let quadrant = null;
  let isSame = true;
  const quadrantList = [];

  if (is384) {
    for (let i = 0; i < tasklist.length; i += 1) {
      const task = tasklist[i];
      const rowTask = convertRowToNumeric(task.row);
      const columnTask = task.column;

      const taskQuadrant = getQuadrant(rowTask, columnTask);
      quadrantList.push(taskQuadrant);

      if (quadrant !== null && quadrant !== taskQuadrant) {
        isSame = false;
        if (!retrieveQuadrant) {
          break;
        }
      }

      quadrant = taskQuadrant;
    }
  }

  if (retrieveQuadrant) {
    return [isSame, selectDistinct(quadrantList)];
  }

  return isSame;
};

// #region Transform Selection
const generateNewValue = (value) => {
  const baseValue = Math.floor(value);
  if (value - baseValue > 0.5) {
    return Math.ceil(value);
  }

  return baseValue;
};

const getTSSize = (wellSize, currentTSSize, is384, initSize) => {
  let row = generateNewValue(currentTSSize.width / wellSize.width);
  let column = generateNewValue(currentTSSize.height / wellSize.height);

  if (is384) {
    if (initSize.row === row) {
      column -= column % 2 === 0 ? 1 : 0;
    }
    if (initSize.column === column) {
      row -= row % 2 === 0 ? 1 : 0;
    }
  }

  return {row, column};
};

const normalizeTSStart = (normalizeSize, tSLayout, isMapping = false, pattern = null, updatedTSLayout = null) => {
  const {labware, start} = tSLayout;
  const is384 = isLabware384(labware);

  const {row: rowSize, column: columnSize} = normalizeSize;
  let {row: rowStart, column: columnStart} = start;

  if (isMapping) {
    const {start: updatedStart} = updatedTSLayout;
    if (pattern === TransformSelectionPattern.COLUMN) {
      rowStart = rowStart - rowSize + 2;
      columnStart -= columnSize - 1;

      if (columnStart <= updatedStart.column && start.column > updatedStart.column) {
        const columnDiff = updatedStart.column - columnStart;
        columnStart = updatedStart.column + (is384 && columnDiff % 2 === 0 ? 2 : 1);
      }
    } else if (pattern === TransformSelectionPattern.ROW) {
      columnStart = columnStart - columnSize + 2;
      rowStart -= rowSize - 1;

      if (rowStart <= updatedStart.row && start.row > updatedStart.row) {
        const rowDiff = updatedStart.row - rowStart;
        rowStart = updatedStart.row + (is384 && rowDiff % 2 === 0 ? 2 : 1);
      }
    }
  }

  if (rowStart < 1) {
    if (is384 && Math.abs(rowStart) % 2 === 0) {
      rowStart = 2;
    } else {
      rowStart = 1;
    }
  }
  if (columnStart < 1) {
    if (is384 && Math.abs(columnStart) % 2 === 0) {
      columnStart = 2;
    } else {
      columnStart = 1;
    }
  }

  if (rowStart + rowSize - 1 > labware.rowCount && !isMapping) {
    // eslint-disable-next-line no-nested-ternary
    rowStart = labware.rowCount - rowSize + (is384 ? (rowStart % 2 === 1 ? 2 : 1) : 0);
  }
  if (columnStart + columnSize - 1 > labware.colCount && !isMapping) {
    // eslint-disable-next-line no-nested-ternary
    columnStart = labware.colCount - columnSize + (is384 ? (columnStart % 2 === 1 ? 2 : 1) : 0);
  }

  if (labware.rowCount === 1) {
    // reset rowStart if labware is 12 well or 1 well trough
    rowStart = 1;
  }
  if (labware.colCount === 1) {
    // reset columnStart if labware is 8 well or 1 well trough
    columnStart = 1;
  }

  return {row: rowStart, column: columnStart};
};

const normalizeTSSize = (size, updatedTSLayout, tSLayout, isMapping = false) => {
  let {row: rowSize, column: columnSize} = size;

  const {labware: updatedLabware, start: updatedStart, size: updatedSize} = updatedTSLayout;
  const {labware: tSLayoutLabware, start: tSLayoutStart} = tSLayout;

  const is384 = isLabware384(tSLayoutLabware);

  if (isMapping) {
    if (is384 && !isLabware384(updatedLabware)) {
      if (updatedSize.column === 1) {
        rowSize = 2 * rowSize - 1;
      }
      if (updatedSize.row === 1) {
        columnSize = 2 * columnSize - 1;

        if (rowSize > tSLayoutLabware.rowCount) {
          rowSize = tSLayoutLabware.rowCount - 1;
        }
      }
    }

    if (updatedLabware.LabwareID === tSLayoutLabware.LabwareID) {
      if (updatedSize.column === 1) {
        if (tSLayoutStart.column > updatedStart.column) {
          if (columnSize > tSLayoutLabware.colCount - updatedStart.column) {
            columnSize = tSLayoutLabware.colCount - updatedStart.column;

            if (is384 && columnSize % 2 === 0) {
              columnSize += 1;
            }
          }
        }
      }
      if (updatedSize.row === 1) {
        if (tSLayoutStart.row > updatedStart.row) {
          if (rowSize > tSLayoutLabware.rowCount - updatedStart.row) {
            rowSize = tSLayoutLabware.rowCount - updatedStart.row;

            if (is384 && rowSize % 2 === 0) {
              rowSize += 1;
            }
          }
        }
      }
    }
  }

  return {row: rowSize, column: columnSize};
};

const getTSStart = (direction, TSLayout, newSize) => {
  const {start: initStart, size: initSize, labware} = TSLayout;

  let {row: rowStart, column: columnStart} = initStart;
  const {row: initRowSize, column: initColumnSize} = initSize;
  const {row: newRowSize, column: newColumnSize} = newSize;

  const {rowCount: rowCountLabware, colCount: columnCountLabware} = labware;

  if (initRowSize === newRowSize && initColumnSize === newColumnSize) {
    return {row: rowStart, column: columnStart};
  }

  let diffRow = newRowSize - initRowSize;
  let diffColumn = newColumnSize - initColumnSize;

  if (isLabware384(labware)) {
    diffRow -= diffRow % 2 === 1 ? 1 : 0;
    diffColumn -= diffColumn % 2 === 1 ? 1 : 0;
  }

  switch (direction) {
    case 'n':
      columnStart -= diffColumn;

      if (columnStart < 1) {
        columnStart = 1;
      }
      break;
    case 's':
      if (columnStart + newColumnSize - 1 > columnCountLabware) {
        columnStart = columnCountLabware - newColumnSize + 1;
      }
      break;
    case 'e':
      rowStart -= diffRow;

      if (rowStart < 1) {
        rowStart = 1;
      }
      break;
    case 'w':
      if (rowStart + newRowSize - 1 > rowCountLabware) {
        rowStart = rowCountLabware - newRowSize + 1;
      }
      break;
    default:
      break;
  }

  const dummyTSLayout = {...TSLayout, start: {row: rowStart, column: columnStart}};

  const normalizeStart = normalizeTSStart(newSize, dummyTSLayout);

  return normalizeStart;
};

const getTSTipCount = (tSLayout, currentSize) => {
  const {labware, size: initSize, tipCount: initTipCount} = tSLayout;

  let {row: rowTipCount, column: columnTipCount} = initTipCount;

  let diffRow = currentSize.row - initSize.row;
  let diffColumn = currentSize.column - initSize.column;

  const wellCount = labware.rowCount * labware.colCount;

  if (wellCount === LabwareWellCount.LABWARE_384) {
    diffRow = Math.floor(diffRow / 2);
    diffColumn = Math.floor(diffColumn / 2);
  }

  rowTipCount += diffRow;
  columnTipCount += diffColumn;

  return {row: rowTipCount, column: columnTipCount};
};

const getUpdatedTSLayoutSize = (tSLayout, currentSize, initSize, updatedLabware) => {
  let diffRow = currentSize.row - initSize.row;
  let diffColumn = currentSize.column - initSize.column;

  const {size, labware} = tSLayout;
  let {row: rowCount, column: columnCount} = size;

  // recalculate diffRow and diffColumn if one of labwares is 384
  if (isLabware384(labware) && !isLabware384(updatedLabware)) {
    diffRow *= 2;
    diffColumn *= 2;
  } else if (!isLabware384(labware) && isLabware384(updatedLabware)) {
    diffRow /= 2;
    diffColumn /= 2;
  }

  rowCount += diffRow;
  columnCount += diffColumn;

  if (labware.rowCount === 1) {
    // reset rowCount if labware is 12 well or 1 well trough
    rowCount = 1;
  } else if (labware.colCount === 1) {
    // reset columnCount if labware is 8 well or 1 well trough
    columnCount = 1;
  }

  return {row: rowCount, column: columnCount};
};

export const generateNewTSLayout = (wellSize, currentTSSize, direction, tSLayout) => {
  const {labware, size: initSize} = tSLayout;
  const size = getTSSize(wellSize, currentTSSize, isLabware384(labware), initSize);
  const start = getTSStart(direction, tSLayout, size);
  const tipCount = getTSTipCount(tSLayout, size);

  return {
    ...tSLayout,
    start,
    size,
    tipCount,
  };
};

export const resizeTSLayout = (initTSLayouts, tSLayout, direction, wellSize, currentTSSize, pattern, twoDmnsTasks) => {
  // resize has 3 different behaviour: increase/decrease tip count, add/remove task, change pattern
  // oneDmnsLayout is source in broadcast or destination in pooling
  // twoDmnsLayout is destination in broadcast or source in pooling
  let oneDmnsnLayout = find(initTSLayouts, {type: TransformSelectionType.ONE_DIMENSION});
  let twoDmnsnLayout = find(initTSLayouts, {type: TransformSelectionType.TWO_DIMENSION});

  const {size: initSize, type} = tSLayout;

  const updatedTSLayout = generateNewTSLayout(wellSize, currentTSSize, direction, tSLayout);

  const {start: updatedStart, size: updatedSize, tipCount: updatedTipCount, labware: updatedLabware} = updatedTSLayout;

  let isAddOrRemoved = false;
  let isMapping = false;

  if (type === TransformSelectionType.ONE_DIMENSION) {
    oneDmnsnLayout = {
      ...oneDmnsnLayout,
      tipCount: updatedTipCount,
      start: updatedStart,
      size: updatedSize,
    };

    if (
      initSize.row === 1 &&
      initSize.column === 1 &&
      twoDmnsTasks.length > 1 &&
      (direction === 'n' || direction === 's') &&
      pattern === TransformSelectionPattern.ROW
    ) {
      // change pattern (only apply in broadcast)
      isMapping = true;
      const normalizeSize = normalizeTSSize(
        {row: twoDmnsnLayout.size.column, column: updatedTipCount.column},
        updatedTSLayout,
        twoDmnsnLayout,
        true,
      );

      const normalizeStart = normalizeTSStart(normalizeSize, twoDmnsnLayout, true, pattern, updatedTSLayout);

      twoDmnsnLayout = {
        ...twoDmnsnLayout,
        tipCount: updatedTipCount,
        size: normalizeSize,
        start: normalizeStart,
      };
    } else if (
      initSize.row === 1 &&
      initSize.column === 1 &&
      twoDmnsTasks.length > 1 &&
      (direction === 'e' || direction === 'w') &&
      pattern === TransformSelectionPattern.COLUMN
    ) {
      // change pattern (only apply in broadcast)
      isMapping = true;
      const normalizeSize = normalizeTSSize(
        {row: updatedTipCount.row, column: twoDmnsnLayout.size.row},
        updatedTSLayout,
        twoDmnsnLayout,
        true,
      );

      const normalizeStart = normalizeTSStart(normalizeSize, twoDmnsnLayout, true, pattern, updatedTSLayout);

      twoDmnsnLayout = {
        ...twoDmnsnLayout,
        tipCount: updatedTipCount,
        size: normalizeSize,
        start: normalizeStart,
      };
    } else {
      // increase/decrease tip count
      const updatedTwoDmnsSize = getUpdatedTSLayoutSize(twoDmnsnLayout, updatedSize, initSize, updatedLabware);
      const updatedTwoDmnsStart = getTSStart(direction, twoDmnsnLayout, updatedTwoDmnsSize);

      twoDmnsnLayout = {
        ...twoDmnsnLayout,
        tipCount: updatedTipCount,
        size: updatedTwoDmnsSize,
        start: updatedTwoDmnsStart,
      };
    }
  } else if (type === TransformSelectionType.TWO_DIMENSION) {
    const sampleTask = twoDmnsTasks[0];
    const {labware: oneDmnsLabware} = oneDmnsnLayout;
    const labwareWellCount = oneDmnsLabware.rowCount * oneDmnsLabware.colCount;

    const isPoolingSingleWellDestination =
      sampleTask.type === TaskType.ASPIRATE && oneDmnsnLayout.size.row === 1 && oneDmnsnLayout.size.column === 1;
    const isBroadcastSingleWellDestination =
      sampleTask.type === TaskType.DISPENSE &&
      oneDmnsnLayout.size.row === 1 &&
      oneDmnsnLayout.size.column === 1 &&
      ((oneDmnsnLayout.labware.rowCount > 1 &&
        oneDmnsnLayout.labware.colCount > 1 &&
        twoDmnsnLayout.size.row > 1 &&
        twoDmnsnLayout.size.column > 1) ||
        (twoDmnsnLayout.size.row === 1 && twoDmnsnLayout.size.column === 1));

    const isSingleWell = isPoolingSingleWellDestination || isBroadcastSingleWellDestination;

    const increaseDecreaseTipCount =
      labwareWellCount === LabwareWellCount.LABWARE_1 ||
      (labwareWellCount === LabwareWellCount.LABWARE_8 && (direction === 'n' || direction === 's')) ||
      (labwareWellCount === LabwareWellCount.LABWARE_12 && (direction === 'e' || direction === 'w'));

    if (
      !isSingleWell &&
      (increaseDecreaseTipCount ||
        ((direction === 'n' || direction === 's') && pattern !== TransformSelectionPattern.ROW) ||
        ((direction === 'e' || direction === 'w') && pattern !== TransformSelectionPattern.COLUMN))
    ) {
      // increase/decrease tip count
      twoDmnsnLayout = {
        ...twoDmnsnLayout,
        tipCount: updatedTipCount,
        start: updatedStart,
        size: updatedSize,
      };

      const updatedOneDmnsSize = getUpdatedTSLayoutSize(oneDmnsnLayout, updatedSize, initSize, updatedLabware);
      const updatedOneDmnsStart = getTSStart(direction, oneDmnsnLayout, updatedOneDmnsSize);

      oneDmnsnLayout = {
        ...oneDmnsnLayout,
        tipCount: updatedTipCount,
        size: updatedOneDmnsSize,
        start: updatedOneDmnsStart,
      };
    } else if (
      isSingleWell &&
      (((direction === 'n' || direction === 's') && pattern === TransformSelectionPattern.COLUMN) ||
        ((direction === 'e' || direction === 'w') && pattern === TransformSelectionPattern.ROW) ||
        labwareWellCount === LabwareWellCount.LABWARE_1)
    ) {
      // increase/decrease tip count on single well
      twoDmnsnLayout = {
        ...twoDmnsnLayout,
        tipCount: updatedTipCount,
        start: updatedStart,
        size: updatedSize,
      };

      const updatedOneDmnsSize = getUpdatedTSLayoutSize(oneDmnsnLayout, updatedSize, initSize, updatedLabware);
      const updatedOneDmnsStart = getTSStart(direction, oneDmnsnLayout, updatedOneDmnsSize);

      oneDmnsnLayout = {
        ...oneDmnsnLayout,
        tipCount: updatedTipCount,
        size: updatedOneDmnsSize,
        start: updatedOneDmnsStart,
      };
    } else if (
      isSingleWell ||
      ((direction === 'n' || direction === 's') && pattern !== TransformSelectionPattern.COLUMN) ||
      ((direction === 'e' || direction === 'w') && pattern !== TransformSelectionPattern.ROW)
    ) {
      // add/remove task
      isAddOrRemoved = true;

      twoDmnsnLayout = {
        ...twoDmnsnLayout,
        start: updatedStart,
        size: updatedSize,
        tipCount: updatedTipCount,
      };
    }
  }

  return {oneDmnsnLayout, twoDmnsnLayout, isMapping, isAddOrRemoved};
};

export const highlightResizeTSLayout = (tSLayout, direction, wellSize, currentTSSize) => {
  const updatedTSLayout = generateNewTSLayout(wellSize, currentTSSize, direction, tSLayout);

  const {start: initStart, size: initSize, labware} = tSLayout;
  const {start: updatedStart, size: updatedSize} = updatedTSLayout;

  const is384 = isLabware384(labware);

  let rowDiff = updatedSize.row - initSize.row;
  let columnDiff = updatedSize.column - initSize.column;

  let newRowStart = updatedStart.row;
  let newColumnStart = updatedStart.column;

  let newRowSize = updatedSize.row;
  let newColumnSize = updatedSize.column;

  // update start
  if (initStart.row === updatedStart.row && initStart.column === updatedStart.column) {
    if (Math.abs(rowDiff) > 0) {
      newRowStart += Math.max(updatedSize.row, initSize.row) - Math.abs(rowDiff) + (is384 ? 1 : 0);
    } else if (Math.abs(columnDiff) > 0) {
      newColumnStart += Math.max(updatedSize.column, initSize.column) - Math.abs(columnDiff) + (is384 ? 1 : 0);
    }
  } else if (rowDiff < 0) {
    newRowStart = initStart.row;
  } else if (columnDiff < 0) {
    newColumnStart = initStart.column;
  }

  if (is384) {
    if (direction === 'e' || direction === 'w') {
      rowDiff = Math.floor(rowDiff / 2);
    }
    if (direction === 'n' || direction === 's') {
      columnDiff = Math.floor(columnDiff / 2);
    }

    newRowSize = Math.ceil(newRowSize / 2);
    newColumnSize = Math.ceil(newColumnSize / 2);
  }

  // update size
  if (Math.abs(rowDiff) > 0) {
    newRowSize = Math.abs(rowDiff);
  } else if (Math.abs(columnDiff) > 0) {
    newColumnSize = Math.abs(columnDiff);
  }

  return {
    ...updatedTSLayout,
    start: {row: newRowStart, column: newColumnStart},
    size: {row: newRowSize, column: newColumnSize},
    difference: {row: rowDiff, column: columnDiff},
  };
};

export const getDragHandlePosition = (targetLabware, draggedLayout, draggedHandle) => {
  const {start, size, labware: originLabware} = draggedLayout;
  const {row: draggedRow, column: draggedCol} = draggedHandle;

  const targetLabwareWellCount = targetLabware.colCount * targetLabware.rowCount;

  const originLabwareWellCount = originLabware.colCount * originLabware.rowCount;

  // ignore tip count in trough labware
  if (originLabwareWellCount === LabwareWellCount.LABWARE_1) {
    return [0, 0, 0, 0];
  }

  // count side item of draggedHandle
  let rightItem = draggedRow - start.row;
  let leftItem = size.row - (rightItem + 1);
  let topItem = draggedCol - start.column;
  let bottomItem = size.column - (topItem + 1);

  if (originLabwareWellCount === LabwareWellCount.LABWARE_8) {
    topItem = 0;
    bottomItem = 0;
  } else if (originLabwareWellCount === LabwareWellCount.LABWARE_12) {
    rightItem = 0;
    leftItem = 0;
  }

  if (targetLabwareWellCount !== originLabwareWellCount && targetLabwareWellCount === LabwareWellCount.LABWARE_384) {
    rightItem *= 2;
    leftItem *= 2;
    topItem *= 2;
    bottomItem *= 2;
  } else if (
    targetLabwareWellCount !== originLabwareWellCount &&
    originLabwareWellCount === LabwareWellCount.LABWARE_384
  ) {
    rightItem = (rightItem + (rightItem % 2 === 0 ? 0 : 1)) / 2;
    leftItem = (leftItem + (leftItem % 2 === 0 ? 0 : 1)) / 2;
    topItem = (topItem + (topItem % 2 === 0 ? 0 : 1)) / 2;
    bottomItem = (bottomItem + (bottomItem % 2 === 0 ? 0 : 1)) / 2;
  }

  return [rightItem, leftItem, topItem, bottomItem];
};

export const getDragDropRowCol = (
  targetRow,
  targetCol,
  rightItem,
  leftItem,
  topItem,
  bottomItem,
  // eslint-disable-next-line default-param-last
  ignoreRowQuadrant = false,
  // eslint-disable-next-line default-param-last
  ignoreColumnQuadrant = false,
  draggedLayout,
  targetLabware,
  sourceLabware,
) => {
  let rowSize = draggedLayout.size.row;
  let columnSize = draggedLayout.size.column;
  let rowStart;
  let columnStart;

  const targetLabwareWellCount = targetLabware.colCount * targetLabware.rowCount;
  const targetLabwareRowCount = targetLabware.rowCount;
  const targetLabwareColCount = targetLabware.colCount;
  const isTarget384 = targetLabwareWellCount === LabwareWellCount.LABWARE_384;

  const sourceLabwareWellCount = sourceLabware.colCount * sourceLabware.rowCount;

  if (draggedLayout.type === TransformSelectionType.ONE_DIMENSION && sourceLabware.isTrough) {
    rowSize = sourceLabware.rowCount === 1 ? 1 : rowSize;
    columnSize = sourceLabware.colCount === 1 ? 1 : columnSize;
  }

  if (draggedLayout.type === TransformSelectionType.TWO_DIMENSION && draggedLayout.labware.isTrough) {
    rowSize = rowSize > draggedLayout.tipCount.row ? rowSize : draggedLayout.tipCount.row;
    columnSize = columnSize > draggedLayout.tipCount.column ? columnSize : draggedLayout.tipCount.column;
  }

  if (sourceLabwareWellCount !== targetLabwareWellCount && sourceLabwareWellCount === LabwareWellCount.LABWARE_384) {
    rowSize = (rowSize + 1) / 2;
    columnSize = (columnSize + 1) / 2;
  } else if (sourceLabwareWellCount !== targetLabwareWellCount && isTarget384) {
    rowSize = rowSize * 2 - 1;
    columnSize = columnSize * 2 - 1;
  }

  rowStart = targetRow - rightItem;
  columnStart = targetCol - topItem;

  if (targetRow <= rightItem || (!ignoreRowQuadrant && rowSize >= 15)) {
    rowStart = 1;
    if (!ignoreRowQuadrant && isTarget384 && targetRow % 2 === 0) {
      rowStart = 2;
    }
  }
  if (targetCol <= topItem || (!ignoreColumnQuadrant && columnSize >= 23)) {
    columnStart = 1;
    if (!ignoreColumnQuadrant && isTarget384 && targetCol % 2 === 0) {
      columnStart = 2;
    }
  }

  if (rowSize < 15 && targetLabwareRowCount > leftItem && targetRow > targetLabwareRowCount - leftItem) {
    rowStart = targetLabwareRowCount - rowSize + (!ignoreRowQuadrant && isTarget384 && targetRow % 2 === 1 ? 0 : 1);
  } else if (rowSize >= 16) {
    rowStart = 1;
  }
  if (columnSize < 23 && targetLabwareColCount > bottomItem && targetCol > targetLabwareColCount - bottomItem) {
    columnStart =
      targetLabwareColCount - columnSize + (!ignoreColumnQuadrant && isTarget384 && targetCol % 2 === 1 ? 0 : 1);
  } else if (columnSize >= 24) {
    columnStart = 1;
  }

  if (isTarget384) {
    rowSize = Math.ceil(rowSize / 2);
    columnSize = Math.ceil(columnSize / 2);
  }

  return [rowStart, columnStart, rowSize, columnSize];
};

export const getNewStartFromTaskList = (
  difference,
  taskList,
  labware,
  originLabware,
  draggedCorner,
  isNormalizeTrough = false,
) => {
  const startList = [];

  // eslint-disable-next-line no-unsafe-optional-chaining
  const isTarget384 = labware?.colCount * labware?.rowCount === 384;
  // eslint-disable-next-line no-unsafe-optional-chaining
  const isOrigin384 = originLabware?.colCount * originLabware?.rowCount === 384;

  taskList.forEach((task) => {
    const rowTask = convertRowToNumeric(task.row);
    const columnTask = task.column;

    let draggedCornerRowDiff = Math.abs(draggedCorner.row - rowTask);
    let draggedCornerColDiff = Math.abs(draggedCorner.column - task.column);

    if (isOrigin384 && !isTarget384) {
      draggedCornerRowDiff /= 2;
      draggedCornerColDiff /= 2;
    }

    const rowIncrementer =
      // eslint-disable-next-line no-nested-ternary
      (isTarget384 && !isOrigin384 ? 1 : isOrigin384 && !isTarget384 ? -1 : 0) * draggedCornerRowDiff;
    const columnIncrementer =
      // eslint-disable-next-line no-nested-ternary
      (isTarget384 && !isOrigin384 ? 1 : isOrigin384 && !isTarget384 ? -1 : 0) * draggedCornerColDiff;

    const newRow = rowTask - difference.row + rowIncrementer;
    const newColumn = columnTask - difference.column + columnIncrementer;

    const normalizeRowTipCount = isNormalizeTrough && originLabware?.rowCount === 1 ? 1 : task.rowTipCount;
    const normalizeColumnTipCount = isNormalizeTrough && originLabware?.colCount === 1 ? 1 : task.columnTipCount;

    const [normalizedRow, normalizedColumn] = normalizeHighlightedWell(
      newRow,
      newColumn,
      {row: normalizeRowTipCount, column: normalizeColumnTipCount},
      labware,
    );

    startList.push({
      row: normalizedRow,
      column: normalizedColumn,
      tipCount: {row: normalizeRowTipCount, column: normalizeColumnTipCount},
    });
  });

  return startList;
};

// #endregion Transform Selection

// #region IFC plate

export const getIFCLabwareTipCount = (labware, index) => {
  const {labwareIndexes} = labware;

  if (!labwareIndexes) {
    return [labware.rowCount, labware.colCount];
  }

  const indexLabware = labwareIndexes[index];
  const {rowCount, colCount} = indexLabware;

  return [rowCount, colCount];
};

export const isIFCLabwareAllowed = (rowTipCount, columnTipCount, labware, index) => {
  if (!labware.isIFC) {
    return true;
  }
  const [rowCount, colCount] = getIFCLabwareTipCount(labware, index);

  const adjustedRowTipCount = 2 * rowTipCount - 1;
  const adjustedColumnTipCount = 2 * columnTipCount - 1;

  if (adjustedRowTipCount > rowCount || adjustedColumnTipCount > colCount) {
    return false;
  }

  return true;
};
// #endregion IFC plate

export default {};

export function getWellDepth(labware) {
  return parseFloat((Math.floor((labware?.wellDepth ?? 0) * 100) / 100).toFixed(2));
}

const FALLBACK_DECK_NUMBER = '2';

export const getDeckNumber = (labwareID) => {
  const {labwareState} = GlobalReduxStore.getState();
  const deckNumber =
    Object.values(labwareState).findIndex((labware) => labware.LabwareID === labwareID) + 1 || FALLBACK_DECK_NUMBER;

  return deckNumber;
};

export const getWellBackgroundColor = (style) => {
  let wellColor = '';
  if (Array.isArray(style?.background)) {
    let sumVol = 0;
    for (let indexT = 0; indexT < style?.background.length; indexT += 1) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const {volume} = style?.background[indexT];
      sumVol += volume;
    }
    let lastPercentage = 0;
    wellColor = 'linear-gradient(';
    for (let indexT = 0; indexT < style?.background.length; indexT += 1) {
      const wellConfig = style?.background[indexT];
      const percentage = lastPercentage + (wellConfig.volume / sumVol) * 100;
      wellColor += `${wellConfig.background} ${lastPercentage}% ${percentage}%,`;
      lastPercentage = percentage;
    }
    wellColor = wellColor.substr(0, wellColor.length - 1);
    wellColor += ')';
  } else {
    wellColor = style?.background;
  }

  return wellColor;
};

export const mergeWellStyle = (style, newStyle) => {
  if (!newStyle) return style;

  const isCurrentlyHovered = style?.background === HOVERED_WELL_COLOR;

  // prioritize hovered well
  if (isCurrentlyHovered) return style;

  if (newStyle.border && style.border) {
    if (style.border !== INACTIVE_WELL_COLOR && newStyle.border === INACTIVE_WELL_COLOR) {
      return style;
    }
  }

  if (newStyle.background && style.background) {
    if (Array.isArray(style.background)) {
      if (Array.isArray(newStyle.background)) {
        return {...style, background: [...style.background, ...newStyle.background]};
      }
      return style;
    }
    if (Array.isArray(newStyle.background)) {
      return {...style, background: newStyle.background};
    }
  }
  return {...style, ...newStyle};
};

export const checkIs24Or48WellLabware = (labware) => {
  const is24Or48WellLabware = [WellLabel.WELL_24, WellLabel.WELL_48].includes(labware.labwareLabel);
  return is24Or48WellLabware;
};

export const is24Or48WellLabwareAllowed = (rowTipCount, columnTipCount, labware) => {
  const is24Or48WellLabware = checkIs24Or48WellLabware(labware);
  const isSingleTipUse = rowTipCount * columnTipCount === 1;

  if (is24Or48WellLabware) {
    return isSingleTipUse;
  }
  return true;
};

export const isTipCountInLabwareValid = (labware, rowTipCount, columnTipCount) => {
  const isLabwareCherryPickOnly = checkIs24Or48WellLabware(labware);
  const isTrough = isLabwareTrough(labware);

  if (isLabwareCherryPickOnly) {
    const isCherryPickRuleValid = is24Or48WellLabwareAllowed(rowTipCount, columnTipCount, labware);
    return isCherryPickRuleValid;
  }

  if (isTrough) return true;

  return columnTipCount <= labware.colCount && rowTipCount <= labware.rowCount;
};

export const getTipCountInString = (tipChannel, customTipCount) => {
  switch (tipChannel) {
    case TipChannelType.TIP_CHANNEL_96:
      return '96';
    case TipChannelType.TIP_CHANNEL_12:
      return '12';
    case TipChannelType.TIP_CHANNEL_8:
      return '8';
    default:
      return `${customTipCount?.row} x ${customTipCount?.column}`;
  }
};

export const identifyWellID = (wellID) => {
  const [row, column, index, labwareID] = wellID.split('-');

  return {row: Number(row), column: Number(column), index: Number(index), labwareID};
};

/**
 * Generates a well ID based on the provided row and column indices, total columns, and labware index.
 * -
 * IMPORTANT TO NOTE.
 * -
 * BE and UI have an agreement to label the alphabetical column as "Row" and numeric column as "Column".
 * this is in reference to the real labware position, which is in landscape and not portrait (see rea labware for reference).
 * in UI, we are displaying the labware in portrait, and we will render the labware starting from rows then cols, as usual.
 * so this function will do the reversing of that rows and cols for the UI to handle to unify the wellID creation logic
 * -
 * @param {number} params.rowIndex - The index of the row
 * @param {number} params.colIndex - The index of the column
 * @param {number} params.totalColumns - The total number of columns
 * @param {number} [params.labwareIndex=0] - Labware index
 *
 * @returns {Object} An object containing the row, column, labware index, and well ID.
 */
export const generateWellID = ({rowIndex, colIndex, totalColumns, labwareIndex = 0}) => {
  // start from 1, and dependent on labwareIndex
  const col = rowIndex + labwareIndex + 1;

  // alphabetical column are starting from the left instead of right
  const row = totalColumns - colIndex;

  return {row, col, wellID: `${row}-${col}-${labwareIndex}`};
};

export const makeWellInvisible = (style) => {
  const invisibleWellStyle = {
    ...style,
    background: 'none',
    color: 'none',
    border: 'transparent',
    opacity: 0,
  };

  return invisibleWellStyle;
};

export const isColumnBasedSwipe = ({row, column}) => column < row;

export const moveColumnPosition = ({labware, initialPosition, moveCountNumber, skipWellMode = SKIP_WELL_MODE.SKIP}) => {
  const is384 = isLabware384(labware);

  const moveCount = skipWellMode === SKIP_WELL_MODE.SKIP || !is384 ? moveCountNumber : moveCountNumber * 2;
  const newPosition = initialPosition + moveCount;

  if (newPosition > labware.colCount) {
    return initialPosition;
  }

  return newPosition;
};

export const moveRowPosition = ({labware, initialPosition, moveCountNumber, skipWellMode = SKIP_WELL_MODE.SKIP}) => {
  const is384 = isLabware384(labware);

  const moveCount = skipWellMode === SKIP_WELL_MODE.SKIP || !is384 ? moveCountNumber : moveCountNumber * 2;
  const newRowPositionNumber = convertRowToNumeric(initialPosition) + moveCount;
  const newRowPositionAlphabet = convertRowToAlphabet(newRowPositionNumber);

  if (newRowPositionNumber > labware.rowCount) {
    return initialPosition;
  }

  return newRowPositionAlphabet;
};

export const getCombinedWellStyle = (wellIDs, wellStyle) => {
  let combinedWellStyle = {};

  wellIDs.forEach((wellID) => {
    combinedWellStyle = mergeWellStyle(combinedWellStyle, wellStyle[wellID]);
  });

  return {
    backgroundColor: getWellBackgroundColor(combinedWellStyle),
    borderColor: combinedWellStyle.border,
    borderWidth: combinedWellStyle.borderSize,
  };
};

export const createWellColumnGenerator = ({wellEventProps, wellCommonProps, wellStyle, labwareID}) => {
  const generate = (cols, index = 0) => {
    const wellElements = [];
    const wellIDs = [];

    for (let iCol = 0; iCol < cols; iCol += 1) {
      const {wellID} = generateWellID({
        rowIndex: 0,
        colIndex: iCol,
        totalColumns: cols,
        labwareIndex: index,
      });

      const {column, row} = identifyWellID(wellID);

      const {hoveredWell, wellHoverMode} = wellCommonProps;
      const isWellHovered = hoveredWell?.column === column && hoveredWell?.row === row && hoveredWell?.index === index;

      const wellProperties = {
        wellKey: `${wellID}-${labwareID}`,
        ID: wellID,
        style: wellStyle[wellID],
        wellHovered: isWellHovered,
        wellHoverMode,
        ...wellEventProps,
      };

      wellElements.push(
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        <CalculatedWell key={`calc-well-${wellProperties.wellKey}`} {...wellProperties} />,
      );
      wellIDs.push(wellID);
    }

    return {
      wellIDs,
      component: wellElements,
    };
  };

  return {
    generate,
  };
};

export const isPositionOnTrough = (labware, {column, columnTipCount}) => {
  const {isTrough, isSpearBio} = labware;

  if (isSpearBio) {
    return isOnSpearBioTrough(column, columnTipCount);
  }

  return isTrough;
};

export const ensureWellRangeFitLabware = (labware, wellRange = new WellItem()) => {
  const {row, column, rowTipCount, columnTipCount} = wellRange;
  const {rowCount: labwareRowCount, colCount: labwareColCount} = labware;

  const isSingleWellTrough = isLabwareSingleWellTrough(labware);
  if (isSingleWellTrough) return wellRange;

  const isRowPassedAsAlphabet = !isNumber(row);

  const rowToCheck = isRowPassedAsAlphabet ? convertRowToNumeric(row) : row;
  const rowTipCountStep = rowTipCount - 1;
  const columnTipCountStep = columnTipCount - 1;

  const newSelectionPosition = {...wellRange, row: rowToCheck};

  if (rowToCheck + rowTipCountStep > labwareRowCount) {
    newSelectionPosition.row = labwareRowCount - rowTipCountStep;
  }

  if (column + columnTipCountStep > labwareColCount) {
    newSelectionPosition.column = labwareColCount - columnTipCountStep;
  }

  if (newSelectionPosition.row < 1 || newSelectionPosition.column < 1) {
    console.debug('Error on ensuring well range fits');
    return wellRange;
  }

  return {
    ...newSelectionPosition,
    row: isRowPassedAsAlphabet ? convertRowToAlphabet(newSelectionPosition.row) : newSelectionPosition.row,
  };
};

export const identifySwipeDirection = (task1 = {column: 1, row: 1}, task2 = {column: 1, row: 1}) => {
  const result = {column: false, row: false};

  if (!task1 || !task2) return result;

  result.column = task1.column !== task2.column;
  result.row = task1.row !== task2.row;

  return result;
};

export const isSameLabwareHash = (labware1, labware2) => labware1.hash === labware2.hash;
